import { useRef, useState, useEffect, useCallback } from 'react';
import { handleError } from '../../context/errors/errorUtils';

export const useSpeechRecognition = ({ language = 'en' } = {}) => {
  const [speechRecognitionAvailable, setSpeechRecognitionAvailable] = useState(false);
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const fullTranscriptRef = useRef('');

  const getLangFormat = (lang) => {
    const langMap = {
      ru: 'ru-RU',
      de: 'de-DE',
      en: 'en-US',
    };
    return langMap[lang] || 'en-US';
  };

	const handleResult = (event, onTranscript) => {
	  let finalTranscript = '';
	  let interimTranscript = '';

	  for (let i = event.resultIndex; i < event.results.length; ++i) {
		const result = event.results[i];
		const transcript = result[0].transcript;

		if (result.isFinal) {
		  finalTranscript += transcript;
		} else {
		  interimTranscript += transcript;
		}
	  }

	  //console.log('[SpeechRecognition] Interim:', interimTranscript);
	  //console.log('[SpeechRecognition] Final:', finalTranscript);

	  if (finalTranscript) {
		fullTranscriptRef.current += ' ' + finalTranscript;
		const fullText = fullTranscriptRef.current.trim();
		//console.log('[SpeechRecognition] Full transcript:', fullText);
		onTranscript?.(fullText);
	  }
	};


  useEffect(() => {
    const isAvailable = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    setSpeechRecognitionAvailable(isAvailable);
	//console.log('🚀 Проверка доступности распознавания речи!', isAvailable);

    if (isAvailable) {
	  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
	  recognitionRef.current = new SpeechRecognition();
	  recognitionRef.current.continuous = false; // Изменено на false для мобильных
	  recognitionRef.current.interimResults = true;
	  recognitionRef.current.maxAlternatives = 1;
	  recognitionRef.current.lang = getLangFormat(language);
	  
      //console.log('🌐 Установлен язык распознавания:', recognitionRef.current.lang);

      recognitionRef.current.onerror = (event) => {
        handleError({
          code: 'ERROR_SPEECH_RECOGNITION',
          error: new Error(event.error),
          context: {
            module: 'SpeechRecognition',
            message: 'Ошибка распознавания речи',
            type: 'recognition',
            detail: event.error,
          },
          showToUser: false,
        });
      };
	  
      recognitionRef.current.onend = () => {
		//console.warn('[SpeechRecognition] ⚠️ onend вызван');  
		//console.warn('[SpeechRecognition] Проверка пересоздания. Версия 1.0');  
        if (isRecordingRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
			console.error('[SpeechRecognition] ❌ Ошибка перезапуска:', e);  
            handleError({
              code: 'ERROR_SPEECH_RESTART',
              error: e,
              context: {
                module: 'SpeechRecognition',
                message: 'Ошибка при перезапуске распознавания речи',
                type: 'restart',
              },
              showToUser: false,
            });
          }
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          handleError({
            code: 'ERROR_SPEECH_STOP_ON_UNMOUNT',
            error: e,
            context: {
              module: 'SpeechRecognition',
              message: 'Ошибка при остановке распознавания при размонтировании',
              type: 'unmount',
            },
            showToUser: false,
          });
        }
      }
    };
  }, [language]);

  const startSpeechRecognition = useCallback(
    (onTranscript) => {
	  //console.log('[SpeechRecognition] ⏺ Старт распознавания');	
      isRecordingRef.current = true;
      fullTranscriptRef.current = '';

      if (speechRecognitionAvailable && recognitionRef.current) {
        recognitionRef.current.onresult = (event) => handleResult(event, onTranscript);

        try {
          recognitionRef.current.start();
        } catch (e) {
          handleError({
            code: 'ERROR_SPEECH_START',
            error: e,
            context: {
              module: 'SpeechRecognition',
              message: 'Ошибка запуска распознавания речи',
              type: 'start',
            },
            showToUser: false,
          });
        }
      } else {
        handleError({
          code: 'SPEECH_RECOGNITION_UNAVAILABLE',
          error: new Error('Speech recognition unavailable or not initialized'),
          context: {
            module: 'SpeechRecognition',
            message: 'Распознавание речи недоступно или не инициализировано',
            type: 'start',
            available: speechRecognitionAvailable,
          },
          showToUser: false,
        });
      }
    },
    [speechRecognitionAvailable]
  );


  const stopSpeechRecognition = useCallback(() => {
	//console.log('[SpeechRecognition] ⏹ Остановка распознавания');  
    isRecordingRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
		console.error('[SpeechRecognition] ❌ Ошибка остановки:', e);  
        handleError({
          code: 'ERROR_SPEECH_STOP',
          error: e,
          context: {
            module: 'SpeechRecognition',
            message: 'Ошибка остановки распознавания речи',
            type: 'stop',
          },
          showToUser: false,
        });
      }
    }
  }, []);

  return {
    speechRecognitionAvailable,
    startSpeechRecognition,
    stopSpeechRecognition,
  };
};
